import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, MapPin, Users, Loader2, Music2, ChevronLeft,
  Phone, Mail, BedDouble, Check, CreditCard, Clock, CheckCircle2,
  Plus, Minus, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import SquarePaymentForm from '../components/SquarePaymentForm';

// Safari-safe POST request using XMLHttpRequest
function safariSafePost(url, body, includeAuth = true) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    // Include auth token if available and requested
    if (includeAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
    }
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          data
        });
      } catch (e) {
        reject(new Error('Failed to parse response'));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    xhr.send(JSON.stringify(body));
  });
}

export default function WeekendDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Booking opens date - 12:00 noon UK time on 1st April 2026
  const BOOKING_OPENS_DATE = new Date('2026-04-01T12:00:00+01:00'); // BST (UK summer time)
  
  // Check if user is admin - called directly during render
  const checkIsAdmin = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.is_admin === true || payload.role === 'admin';
    } catch {
      return false;
    }
  };
  
  // Check if bookings are open
  const isBookingOpen = () => {
    return new Date() >= BOOKING_OPENS_DATE || checkIsAdmin();
  };
  
  // Get time remaining until booking opens
  const getTimeUntilOpen = () => {
    const now = new Date();
    const diff = BOOKING_OPENS_DATE - now;
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };
  
  // Booking form state
  const [step, setStep] = useState(1); // 1: Rooms, 2: Guests, 3: Confirm, 4: Payment, 5: Success
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    rooms: {}, // { roomType: quantity }
    roomGuestCounts: {}, // { roomType: guestsPerRoom } - for variable occupancy rooms like Family
    guests: [], // [{ first_name, last_name }]
    addOnQuantities: {}, // { addOnName: passengerQuantity }
    special_requests: '',
    payment_type: 'deposit', // 'deposit', 'full', 'deposit_with_plan', or 'no_deposit'
    terms_accepted: false // T&Cs acceptance
  });
  const [booking, setBooking] = useState(null);
  const [errors, setErrors] = useState({});
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentPlanCalc, setPaymentPlanCalc] = useState(null); // For showing monthly amount
  const [noDepositOption, setNoDepositOption] = useState({
    enabled: false,
    userLoggedIn: false,
    canUse: false,
    message: null
  });

  // Check if a room type supports variable guest count
  const isVariableOccupancyRoom = (roomType) => {
    const type = roomType.toLowerCase();
    return type.includes('family');
  };

  // Get min/max guests for variable occupancy rooms
  const getGuestRange = (roomType) => {
    const type = roomType.toLowerCase();
    if (type.includes('family')) {
      return { min: 3, max: 4 };
    }
    return null;
  };

  // Get the actual guest count for a room (considering variable occupancy)
  const getActualGuestsForRoom = (roomType) => {
    if (isVariableOccupancyRoom(roomType)) {
      // Use selected guest count if set, otherwise default to minimum
      if (formData.roomGuestCounts[roomType]) {
        return formData.roomGuestCounts[roomType];
      }
      const range = getGuestRange(roomType);
      return range ? range.min : getRoomOccupancy(roomType);
    }
    return getRoomOccupancy(roomType);
  };

  // Update guest count for variable occupancy rooms
  const updateRoomGuestCount = (roomType, guestCount) => {
    setFormData(prev => ({
      ...prev,
      roomGuestCounts: {
        ...prev.roomGuestCounts,
        [roomType]: guestCount
      }
    }));
  };

  // Define fetchEvent BEFORE useEffect that uses it
  const fetchEvent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
        // Set default room type
        if (data.room_options?.length > 0) {
          setFormData(prev => ({ ...prev, room_type: data.room_options[0].type }));
        }
      } else {
        toast.error('Event not found');
        navigate('/weekends');
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // Fetch no-deposit booking options when event is loaded
  useEffect(() => {
    const fetchBookingOptions = async () => {
      if (!event?.event_id) return;
      
      try {
        const headers = {};
        const token = localStorage.getItem('token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/api/weekends/${event.event_id}/booking-options`, { headers });
        if (response.ok) {
          const data = await response.json();
          setNoDepositOption({
            enabled: data.no_deposit_option_enabled,
            userLoggedIn: data.user_logged_in,
            canUse: data.can_use_no_deposit,
            message: data.message
          });
        }
      } catch (error) {
        console.error('Error fetching booking options:', error);
      }
    };
    
    fetchBookingOptions();
  }, [event?.event_id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const calculateMonthsUntilDate = (endDateStr) => {
    if (!endDateStr) return 0;
    const now = new Date();
    const endDate = new Date(endDateStr);
    let months = 0;
    let checkDate = new Date(now);
    
    while (checkDate < endDate) {
      // Move to 1st of next month
      if (checkDate.getDate() === 1) {
        checkDate.setMonth(checkDate.getMonth() + 1);
      } else {
        checkDate.setMonth(checkDate.getMonth() + 1);
        checkDate.setDate(1);
      }
      if (checkDate <= endDate) {
        months++;
      }
    }
    return months;
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return '';
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    const startStr = startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    const endStr = endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    
    return `${startStr} - ${endStr}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const setAddOnQuantity = (addOnName, qty) => {
    setFormData(prev => {
      const max = getTotalGuests();
      const clamped = Math.max(0, Math.min(qty, max));
      const next = { ...prev.addOnQuantities };
      if (clamped <= 0) {
        delete next[addOnName];
      } else {
        next[addOnName] = clamped;
      }
      return { ...prev, addOnQuantities: next };
    });
  };

  const updateRoomQuantity = (roomType, delta) => {
    setFormData(prev => {
      const currentQty = prev.rooms[roomType] || 0;
      const newQty = Math.max(0, currentQty + delta);
      const newRooms = { ...prev.rooms };
      
      if (newQty === 0) {
        delete newRooms[roomType];
      } else {
        newRooms[roomType] = newQty;
      }
      
      return { ...prev, rooms: newRooms };
    });
  };

  // Room occupancy mapping
  const getRoomOccupancy = (roomType) => {
    const type = roomType.toLowerCase();
    
    // First check for explicit capacity in room_options (source of truth)
    if (event?.room_options) {
      const roomOption = event.room_options.find(r => r.type.toLowerCase() === type);
      if (roomOption?.capacity) return roomOption.capacity;
    }
    
    // Fall back to name-based matching
    if (type.includes('single') && !type.includes('double') && !type.includes('twin') && !type.includes('triple')) return 1;
    if (type.includes('double') || type.includes('twin')) return 2;
    if (type.includes('triple')) return 3;
    if (type.includes('quad') || type.includes('family')) return 4;
    return 2; // Default to 2 for unknown types
  };

  const getTotalGuests = () => {
    if (!event) return 0;
    let total = 0;
    Object.entries(formData.rooms).forEach(([roomType, roomCount]) => {
      const guestsPerRoom = getActualGuestsForRoom(roomType);
      total += roomCount * guestsPerRoom;
    });
    return total;
  };

  const getTotalRooms = () => {
    return Object.values(formData.rooms).reduce((sum, qty) => sum + qty, 0);
  };

  const updateGuestName = (index, field, value) => {
    setFormData(prev => {
      const newGuests = [...prev.guests];
      if (!newGuests[index]) {
        newGuests[index] = { first_name: '', last_name: '' };
      }
      newGuests[index][field] = value;
      return { ...prev, guests: newGuests };
    });
  };

  // Initialize guests array when total changes - use a ref to track if we've initialized
  const prevTotalRef = React.useRef(0);
  useEffect(() => {
    const totalGuests = getTotalGuests();
    // Only update if total actually changed
    if (totalGuests !== prevTotalRef.current) {
      prevTotalRef.current = totalGuests;
      setFormData(prev => {
        const newGuests = [...prev.guests];
        // Only add new guests, preserve existing ones
        while (newGuests.length < totalGuests) {
          newGuests.push({ first_name: '', last_name: '' });
        }
        // Only remove if we have too many (but preserve entered data)
        while (newGuests.length > totalGuests) {
          newGuests.pop();
        }
        return { ...prev, guests: newGuests };
      });
    }
  }, [formData.rooms, formData.roomGuestCounts, event]);

  // Ensure add-on passenger quantities never exceed the current total guest count
  useEffect(() => {
    const totalGuests = getTotalGuests();
    setFormData(prev => {
      let changed = false;
      const next = {};
      Object.entries(prev.addOnQuantities || {}).forEach(([name, qty]) => {
        const clamped = Math.min(qty, totalGuests);
        if (clamped !== qty) changed = true;
        if (clamped > 0) next[name] = clamped;
        else changed = true;
      });
      return changed ? { ...prev, addOnQuantities: next } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.rooms, formData.roomGuestCounts, event]);

  const calculateTotal = () => {
    if (!event) return { rooms: 0, addOns: 0, subtotal: 0, roomDeposit: 0, addOnsDeposit: 0, totalDeposit: 0 };
    
    const totalGuests = getTotalGuests();
    
    // Calculate rooms total (price per person × guests in that room type)
    let roomsTotal = 0;
    Object.entries(formData.rooms).forEach(([roomType, roomCount]) => {
      const roomOption = event.room_options?.find(r => r.type === roomType);
      if (roomOption) {
        const guestsPerRoom = getActualGuestsForRoom(roomType);
        const guestsInRoomType = roomCount * guestsPerRoom;
        roomsTotal += (roomOption.price_per_person || 0) * guestsInRoomType;
      }
    });
    
    // Calculate add-ons total (price × selected passenger quantity)
    let addOnsTotal = 0;
    Object.entries(formData.addOnQuantities).forEach(([addOnName, qty]) => {
      const addOn = event.add_ons?.find(a => a.name === addOnName);
      if (addOn && qty > 0) {
        addOnsTotal += (addOn.price || 0) * qty;
      }
    });
    
    const subtotal = roomsTotal + addOnsTotal;
    
    // Calculate fixed deposits
    const roomDepositPerPerson = event.room_deposit_amount || 50;
    const roomDeposit = roomDepositPerPerson * totalGuests;
    
    let addOnsDeposit = 0;
    Object.entries(formData.addOnQuantities).forEach(([addOnName, qty]) => {
      const addOn = event.add_ons?.find(a => a.name === addOnName);
      if (addOn && addOn.deposit_amount && qty > 0) {
        addOnsDeposit += addOn.deposit_amount * qty;
      }
    });
    
    const totalDeposit = roomDeposit + addOnsDeposit;
    
    return { rooms: roomsTotal, addOns: addOnsTotal, subtotal, roomDeposit, addOnsDeposit, totalDeposit };
  };

  const validateStep1 = () => {
    const totalGuests = getTotalGuests();
    if (totalGuests === 0) {
      toast.error('Please select at least one room');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Name is required';
    }
    if (!formData.customer_email || !/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = 'Valid email is required';
    }
    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = 'Phone number is required';
    }
    
    // Validate T&Cs acceptance
    if (!formData.terms_accepted) {
      newErrors.terms_accepted = 'You must accept the Terms & Conditions to proceed';
    }
    
    // Validate all guest names
    formData.guests.forEach((guest, idx) => {
      if (!guest.first_name?.trim()) {
        newErrors[`guest_${idx}_first`] = 'First name required';
      }
      if (!guest.last_name?.trim()) {
        newErrors[`guest_${idx}_last`] = 'Last name required';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitBooking = async () => {
    if (!validateStep2()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      // Convert rooms object to array format, including guest count for variable rooms
      const roomsArray = Object.entries(formData.rooms)
        .filter(([_, qty]) => qty > 0)
        .map(([room_type, quantity]) => {
          const roomData = { room_type, quantity };
          // Include guests_per_room for variable occupancy rooms
          if (isVariableOccupancyRoom(room_type) && formData.roomGuestCounts[room_type]) {
            roomData.guests_per_room = formData.roomGuestCounts[room_type];
          }
          return roomData;
        });
      
      // Use XMLHttpRequest to avoid Safari "body disturbed" error
      // Auth header is automatically included by safariSafePost
      const response = await safariSafePost(`${API_URL}/api/weekends/bookings`, {
        event_id: event.event_id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        rooms: roomsArray,
        guests: formData.guests,
        add_on_selections: Object.entries(formData.addOnQuantities)
          .filter(([_, qty]) => qty > 0)
          .map(([name, quantity]) => ({ name, quantity })),
        special_requests: formData.special_requests,
        payment_type: formData.payment_type,
        no_deposit: formData.payment_type === 'no_deposit',
        terms_accepted: formData.terms_accepted,
        terms_accepted_at: new Date().toISOString()
      });
      
      if (response.ok) {
        setBooking(response.data);
        
        // For no-deposit bookings, skip to confirmation
        if (formData.payment_type === 'no_deposit') {
          setStep(5); // Skip payment, go to confirmation
          toast.success('Booking confirmed! No payment required at this time.');
        } else {
          setStep(4); // Move to payment step (step 4 now)
          toast.success('Booking created! Please complete payment.');
        }
      } else {
        toast.error(response.data.detail || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calculateTotal();
  const totalGuests = getTotalGuests();

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl text-stone-900 mb-4">Event not found</h2>
          <Button onClick={() => navigate('/weekends')}>
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Hero Section */}
      <section className="relative">
        <div className="h-[40vh] min-h-[300px] relative overflow-hidden">
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-violet-600/50 to-fuchsia-600/50 flex items-center justify-center">
              <Music2 className="w-24 h-24 text-stone-900/30" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />
        </div>
        
        <div className="container mx-auto px-4 -mt-32 relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate('/weekends')}
            className="text-stone-900/70 hover:text-stone-900 mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Events
          </Button>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="event-title">
              {event.title}
            </h1>
            
            <div className="flex flex-wrap gap-4 text-stone-200">
              <div className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-violet-300" />
                {formatDateRange(event.start_date, event.end_date)}
              </div>
              <div className="flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-violet-300" />
                {event.venue_name}
              </div>
              {event.max_capacity && (
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-violet-300" />
                  {event.current_bookings || 0} / {event.max_capacity} booked
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Event Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="bg-white border border-stone-200 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-stone-900 mb-4">About This Event</h2>
                <p className="text-stone-600 whitespace-pre-line">{event.description}</p>
                
                {event.additional_info && (
                  <div className="mt-6 pt-6 border-t border-stone-200">
                    <h3 className="text-lg font-semibold text-stone-900 mb-3">Additional Information</h3>
                    <p className="text-stone-600 whitespace-pre-line">{event.additional_info}</p>
                  </div>
                )}
              </div>

              {/* Venue Details */}
              <div className="bg-white border border-stone-200 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-stone-900 mb-4">Venue</h2>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-violet-600 mt-1" />
                  <div>
                    <p className="text-stone-900 font-medium">{event.venue_name}</p>
                    <p className="text-stone-600">{event.venue_address}</p>
                  </div>
                </div>
              </div>

              {/* Room Options */}
              <div className="bg-white border border-stone-200 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-stone-900 mb-4">Accommodation Options</h2>
                <div className="space-y-4">
                  {event.room_options?.map((room) => (
                    <div key={room.type} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <BedDouble className="w-5 h-5 text-violet-600" />
                        <div>
                          <p className="text-stone-900 font-medium capitalize">{room.type} Room</p>
                          {room.description && (
                            <p className="text-stone-600 text-sm">{room.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-violet-600 font-semibold">£{room.price_per_person}</p>
                        <p className="text-stone-600 text-sm">per person</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              {event.add_ons?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-xl p-6">
                  <h2 className="text-xl font-semibold text-stone-900 mb-4">Optional Add-ons</h2>
                  <div className="space-y-3">
                    {event.add_ons.map((addOn) => (
                      <div key={addOn.name} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                        <div>
                          <p className="text-stone-900 font-medium">{addOn.name}</p>
                          {addOn.description && (
                            <p className="text-stone-600 text-sm">{addOn.description}</p>
                          )}
                        </div>
                        <p className="text-violet-600 font-semibold">+£{addOn.price} pp</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Booking Form */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-stone-200 rounded-xl p-6 sticky top-4">
                <h2 className="text-xl font-semibold text-stone-900 mb-6">
                  {step === 1 && 'Select Rooms'}
                  {step === 2 && 'Guest Details'}
                  {step === 3 && 'Confirm Booking'}
                  {step === 4 && 'Complete Payment'}
                  {step === 5 && 'Payment Successful'}
                </h2>

                {/* Booking Not Yet Open Message */}
                {!isBookingOpen() && (
                  <div className="space-y-4 text-center py-8">
                    <div className="w-16 h-16 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto">
                      <Clock className="w-8 h-8 text-violet-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900">Bookings Opening Soon!</h3>
                    <p className="text-stone-600">
                      Bookings for this event will open on:
                    </p>
                    <div className="bg-stone-50 rounded-lg p-4">
                      <p className="text-violet-600 text-lg font-semibold">
                        Wednesday 1st April 2026
                      </p>
                      <p className="text-stone-900 text-2xl font-bold">
                        12:00 Noon
                      </p>
                    </div>
                    {getTimeUntilOpen() && (
                      <p className="text-stone-600 text-sm">
                        Opens in <span className="text-violet-600 font-medium">{getTimeUntilOpen()}</span>
                      </p>
                    )}
                    <p className="text-stone-600 text-xs">
                      Add this page to your favourites and come back when bookings open!
                    </p>
                  </div>
                )}

                {/* Step 1: Room Selection */}
                {isBookingOpen() && step === 1 && (
                  <div className="space-y-4">
                    <p className="text-stone-600 text-sm mb-4">
                      Select the rooms you need. You can choose multiple room types.
                    </p>
                    
                    {/* Room Options with +/- controls */}
                    <div className="space-y-3">
                      {event.room_options?.map((room, idx) => {
                        const occupancy = getRoomOccupancy(room.type);
                        const roomCount = formData.rooms[room.type] || 0;
                        const available = room.available_count;
                        const booked = room.booked_count || 0;
                        const remaining = available !== null && available !== undefined ? available - booked : null;
                        const isSoldOut = remaining !== null && remaining <= 0;
                        const maxCanBook = remaining !== null ? remaining : 99;
                        const isVariable = isVariableOccupancyRoom(room.type);
                        const guestRange = getGuestRange(room.type);
                        const selectedGuestCount = formData.roomGuestCounts[room.type] || (guestRange?.min || occupancy);
                        
                        return (
                          <div key={idx} className={`p-4 rounded-lg ${isSoldOut ? 'bg-stone-100 opacity-60' : 'bg-stone-50'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-stone-900 font-medium capitalize">{room.type} Room</p>
                                  {isSoldOut && (
                                    <Badge variant="destructive" className="text-xs">Sold Out</Badge>
                                  )}
                                </div>
                                <p className="text-stone-600 text-xs">
                                  {isVariable 
                                    ? `${guestRange.min}-${guestRange.max} people sharing` 
                                    : (occupancy === 1 ? '1 person' : `${occupancy} people sharing`)
                                  }
                                </p>
                                <p className="text-violet-600 text-sm">£{room.price_per_person} per person</p>
                                {room.description && (
                                  <p className="text-stone-600 text-xs mt-1">{room.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateRoomQuantity(room.type, -1)}
                                  disabled={roomCount === 0 || isSoldOut}
                                  data-testid={`room-minus-${room.type}`}
                                  className="h-8 w-8 p-0 border-violet-400 bg-white border border-stone-200 hover:bg-stone-50 text-stone-900"
                                >
                                  <Minus className="w-4 h-4" />
                                </Button>
                                <span className="text-stone-900 w-8 text-center font-medium">
                                  {roomCount}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateRoomQuantity(room.type, 1)}
                                  disabled={isSoldOut || roomCount >= maxCanBook}
                                  data-testid={`room-plus-${room.type}`}
                                  className="h-8 w-8 p-0 border-violet-400 bg-white border border-stone-200 hover:bg-stone-50 text-stone-900"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Guest count selector for variable occupancy rooms */}
                            {isVariable && roomCount > 0 && (
                              <div className="mt-3 pt-3 border-t border-stone-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-stone-600 text-sm">Number of guests in room:</span>
                                  <div className="flex items-center gap-2">
                                    {[...Array(guestRange.max - guestRange.min + 1)].map((_, i) => {
                                      const guestNum = guestRange.min + i;
                                      return (
                                        <Button
                                          key={guestNum}
                                          variant={selectedGuestCount === guestNum ? "default" : "outline"}
                                          size="sm"
                                          onClick={() => updateRoomGuestCount(room.type, guestNum)}
                                          className={`h-8 w-8 p-0 ${selectedGuestCount === guestNum ? 'bg-violet-600 hover:bg-violet-700 text-white' : 'border-violet-400 bg-white border border-stone-200 hover:bg-stone-50 text-stone-700'}`}
                                        >
                                          {guestNum}
                                        </Button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <p className="text-stone-600 text-xs mt-1">
                                  Total: £{(room.price_per_person * selectedGuestCount).toFixed(2)} for {selectedGuestCount} guest{selectedGuestCount > 1 ? 's' : ''}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Add-ons */}
                    {event.add_ons?.length > 0 && (
                      <div>
                        <Label className="text-stone-900">Optional Add-ons</Label>
                        <p className="text-stone-500 text-xs mt-1">Choose how many passengers need each extra (up to {totalGuests} guest{totalGuests !== 1 ? 's' : ''}).</p>
                        <div className="mt-2 space-y-2">
                          {event.add_ons.map((addOn, idx) => {
                            const qty = formData.addOnQuantities[addOn.name] || 0;
                            const lineTotal = (addOn.price || 0) * qty;
                            return (
                            <div
                              key={idx}
                              className="p-3 bg-stone-50 rounded-lg"
                              data-testid={`public-addon-row-${addOn.name}`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1">
                                  <span className="text-stone-900">{addOn.name}</span>
                                  <span className="text-violet-600 ml-2">+£{addOn.price} pp</span>
                                  {addOn.deposit_amount > 0 && (
                                    <span className="text-stone-600 text-xs block">
                                      (£{addOn.deposit_amount} deposit pp)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setAddOnQuantity(addOn.name, qty - 1)}
                                    disabled={qty === 0}
                                    className="h-8 w-8 p-0 border-violet-400 bg-white border border-stone-200 hover:bg-stone-50 text-stone-900"
                                    data-testid={`public-addon-minus-${addOn.name}`}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  <span className="text-stone-900 w-8 text-center font-medium" data-testid={`public-addon-qty-${addOn.name}`}>
                                    {qty}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setAddOnQuantity(addOn.name, qty + 1)}
                                    disabled={totalGuests === 0 || qty >= totalGuests}
                                    className="h-8 w-8 p-0 border-violet-400 bg-white border border-stone-200 hover:bg-stone-50 text-stone-900"
                                    data-testid={`public-addon-plus-${addOn.name}`}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              {qty > 0 && (
                                <p className="text-stone-600 text-xs mt-2">
                                  {qty} passenger{qty !== 1 ? 's' : ''} × £{addOn.price} = £{lineTotal.toFixed(2)}
                                </p>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Price Summary */}
                    {totalGuests > 0 && (
                      <div className="border-t border-stone-200 pt-4 mt-4">
                        <div className="space-y-2 text-sm">
                          {/* Room breakdown */}
                          {Object.entries(formData.rooms).filter(([_, qty]) => qty > 0).map(([roomType, qty]) => {
                            const roomOption = event.room_options?.find(r => r.type === roomType);
                            const guestsPerRoom = getActualGuestsForRoom(roomType);
                            const guests = qty * guestsPerRoom;
                            const price = (roomOption?.price_per_person || 0) * guests;
                            return (
                              <div key={roomType} className="flex justify-between text-stone-600">
                                <span className="capitalize">{qty}× {roomType} ({guests} guest{guests > 1 ? 's' : ''})</span>
                                <span>£{price.toFixed(2)}</span>
                              </div>
                            );
                          })}
                          {Object.entries(formData.addOnQuantities).filter(([_, qty]) => qty > 0).map(([addOnName, qty]) => {
                            const addOn = event.add_ons?.find(a => a.name === addOnName);
                            if (!addOn) return null;
                            return (
                              <div key={addOnName} className="flex justify-between text-stone-600">
                                <span>{addOnName} ({qty} passenger{qty !== 1 ? 's' : ''})</span>
                                <span>£{((addOn.price || 0) * qty).toFixed(2)}</span>
                              </div>
                            );
                          })}
                          <div className="flex justify-between text-stone-900 font-semibold pt-2 border-t border-stone-200">
                            <span>Total ({getTotalRooms()} room{getTotalRooms() > 1 ? 's' : ''}, {totalGuests} guest{totalGuests > 1 ? 's' : ''})</span>
                            <span>£{totals.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="pt-2 border-t border-stone-200 space-y-1">
                            <div className="flex justify-between text-stone-600 text-sm">
                              <span>Room Deposit (£{event.room_deposit_amount || 50} × {totalGuests})</span>
                              <span>£{totals.roomDeposit.toFixed(2)}</span>
                            </div>
                            {totals.addOnsDeposit > 0 && (
                              <div className="flex justify-between text-stone-600 text-sm">
                                <span>Add-ons Deposit</span>
                                <span>£{totals.addOnsDeposit.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between text-violet-600 font-medium">
                              <span>Total Deposit</span>
                              <span>£{totals.totalDeposit.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={() => {
                        if (validateStep1()) {
                          setStep(2);
                        }
                      }}
                      disabled={totalGuests === 0}
                      className="w-full bg-violet-600 hover:bg-violet-500"
                      data-testid="booking-continue-btn"
                    >
                      Continue to Guest Details
                    </Button>
                  </div>
                )}

                {/* Step 2: Guest Details */}
                {step === 2 && (
                  <div className="space-y-4">
                    {/* Contact Info */}
                    <div className="space-y-3">
                      <h3 className="text-stone-900 font-medium">Contact Information</h3>
                      <div>
                        <Label htmlFor="customer_name" className="text-stone-900 text-sm">Your Full Name *</Label>
                        <Input
                          id="customer_name"
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          className={`bg-white border-stone-200 text-stone-900 ${errors.customer_name ? 'border-red-500' : ''}`}
                          placeholder="Your full name"
                          data-testid="booking-name-input"
                        />
                        {errors.customer_name && (
                          <p className="text-red-400 text-xs mt-1">{errors.customer_name}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="customer_email" className="text-stone-900 text-sm">Email *</Label>
                        <Input
                          id="customer_email"
                          name="customer_email"
                          type="email"
                          value={formData.customer_email}
                          onChange={handleInputChange}
                          className={`bg-white border-stone-200 text-stone-900 ${errors.customer_email ? 'border-red-500' : ''}`}
                          placeholder="your@email.com"
                          data-testid="booking-email-input"
                        />
                        {errors.customer_email && (
                          <p className="text-red-400 text-xs mt-1">{errors.customer_email}</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="customer_phone" className="text-stone-900 text-sm">Phone *</Label>
                        <Input
                          id="customer_phone"
                          name="customer_phone"
                          value={formData.customer_phone}
                          onChange={handleInputChange}
                          className={`bg-white border-stone-200 text-stone-900 ${errors.customer_phone ? 'border-red-500' : ''}`}
                          placeholder="Your phone number"
                          data-testid="booking-phone-input"
                        />
                        {errors.customer_phone && (
                          <p className="text-red-400 text-xs mt-1">{errors.customer_phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Guest Names */}
                    <div className="space-y-3 pt-4 border-t border-stone-200">
                      <h3 className="text-stone-900 font-medium flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Guest Names ({totalGuests} guest{totalGuests > 1 ? 's' : ''})
                      </h3>
                      <p className="text-stone-600 text-xs">
                        Please enter the names of all guests attending
                      </p>
                      <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                        {formData.guests.map((guest, idx) => (
                          <div key={idx} className="p-3 bg-stone-50 rounded-lg">
                            <p className="text-stone-600 text-xs mb-2 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Guest {idx + 1}
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Input
                                  value={guest.first_name || ''}
                                  onChange={(e) => updateGuestName(idx, 'first_name', e.target.value)}
                                  className={`bg-white border-stone-200 text-stone-900 text-sm ${errors[`guest_${idx}_first`] ? 'border-red-500' : ''}`}
                                  placeholder="First name"
                                />
                              </div>
                              <div>
                                <Input
                                  value={guest.last_name || ''}
                                  onChange={(e) => updateGuestName(idx, 'last_name', e.target.value)}
                                  className={`bg-white border-stone-200 text-stone-900 text-sm ${errors[`guest_${idx}_last`] ? 'border-red-500' : ''}`}
                                  placeholder="Last name"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Special Requests */}
                    <div>
                      <Label htmlFor="special_requests" className="text-stone-900 text-sm">Special Requests</Label>
                      <Textarea
                        id="special_requests"
                        name="special_requests"
                        value={formData.special_requests}
                        onChange={handleInputChange}
                        className="bg-white border-stone-200 text-stone-900"
                        placeholder="Dietary requirements, accessibility needs, etc."
                        rows={2}
                      />
                    </div>

                    {/* Terms & Conditions */}
                    <div className={`bg-stone-50 rounded-lg p-4 border ${errors.terms_accepted ? 'border-red-500' : 'border-stone-200'}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="terms_accepted"
                          checked={formData.terms_accepted}
                          onCheckedChange={(checked) => {
                            setFormData(prev => ({ ...prev, terms_accepted: checked }));
                            if (checked && errors.terms_accepted) {
                              setErrors(prev => ({ ...prev, terms_accepted: undefined }));
                            }
                          }}
                          className="mt-0.5 border-stone-300 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                        />
                        <label htmlFor="terms_accepted" className="text-sm text-stone-600 cursor-pointer leading-relaxed">
                          I confirm that I am the Lead Booker, aged 18 or over, and I have read and accept the{' '}
                          <a 
                            href="/weekends/terms" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-violet-600 hover:text-violet-300 underline"
                          >
                            Booking Terms & Conditions
                          </a>
                          {' '}on behalf of myself and all guests included in this booking.
                        </label>
                      </div>
                      {errors.terms_accepted && (
                        <p className="text-red-400 text-xs mt-2 ml-7">{errors.terms_accepted}</p>
                      )}
                      <p className="text-stone-500 text-xs mt-2 ml-7">
                        The £{event.room_deposit_amount || 50} deposit per person is non-refundable. We recommend travel insurance.
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="flex-1 border-stone-200 text-stone-900 hover:bg-white"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={() => {
                          if (validateStep2()) {
                            setStep(3);
                          } else {
                            toast.error('Please fill in all required fields');
                          }
                        }}
                        className="flex-1 bg-violet-600 hover:bg-violet-500"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Confirmation */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="bg-stone-50 rounded-lg p-4 space-y-3">
                      <h3 className="text-stone-900 font-medium">Booking Summary</h3>
                      <div className="text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-stone-600">Contact</span>
                          <span className="text-stone-900">{formData.customer_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-600">Email</span>
                          <span className="text-stone-900 text-xs">{formData.customer_email}</span>
                        </div>
                        <div className="pt-2 border-t border-stone-200">
                          <span className="text-stone-600 text-xs">Rooms:</span>
                          {Object.entries(formData.rooms).filter(([_, qty]) => qty > 0).map(([type, qty]) => (
                            <div key={type} className="flex justify-between">
                              <span className="text-stone-900 capitalize">{qty}× {type}</span>
                              <span className="text-stone-600">
                                £{((event.room_options?.find(r => r.type === type)?.price_per_person || 0) * getActualGuestsForRoom(type) * qty).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="pt-2 border-t border-stone-200">
                          <span className="text-stone-600 text-xs">Guests:</span>
                          {formData.guests.map((guest, idx) => (
                            <p key={idx} className="text-stone-900 text-sm">
                              {guest.first_name} {guest.last_name}
                            </p>
                          ))}
                        </div>
                        {Object.keys(formData.addOnQuantities).length > 0 && (
                          <div className="pt-2 border-t border-stone-200">
                            <span className="text-stone-600 text-xs">Add-ons:</span>
                            <p className="text-stone-900 text-sm">
                              {Object.entries(formData.addOnQuantities)
                                .filter(([_, qty]) => qty > 0)
                                .map(([name, qty]) => `${name} × ${qty}`)
                                .join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment Type Selection */}
                    <div>
                      <Label className="text-stone-900">Payment Option</Label>
                      <div className="mt-2 space-y-2">
                        <div 
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border ${formData.payment_type === 'deposit' ? 'border-violet-500 bg-violet-500/10' : 'border-stone-200 bg-stone-50'}`}
                          onClick={() => setFormData(prev => ({ ...prev, payment_type: 'deposit' }))}
                        >
                          <span className="text-stone-900">Pay Deposit Only</span>
                          <span className="text-violet-600 font-semibold">£{totals.totalDeposit.toFixed(2)}</span>
                        </div>
                        <div 
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border ${formData.payment_type === 'full' ? 'border-violet-500 bg-violet-500/10' : 'border-stone-200 bg-stone-50'}`}
                          onClick={() => setFormData(prev => ({ ...prev, payment_type: 'full' }))}
                        >
                          <span className="text-stone-900">Pay in Full</span>
                          <span className="text-violet-600 font-semibold">£{totals.subtotal.toFixed(2)}</span>
                        </div>
                        {/* Payment Plan Option - only show if event has payment_plan_end_date */}
                        {event?.payment_plan_end_date && totals.subtotal > totals.totalDeposit && (
                          <div 
                            className={`p-3 rounded-lg cursor-pointer border ${formData.payment_type === 'deposit_with_plan' ? 'border-violet-500 bg-violet-500/10' : 'border-stone-200 bg-stone-50'}`}
                            onClick={() => setFormData(prev => ({ ...prev, payment_type: 'deposit_with_plan' }))}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-stone-900">Pay Deposit + Set Up Payment Plan</span>
                              <span className="text-violet-600 font-semibold">£{totals.totalDeposit.toFixed(2)}</span>
                            </div>
                            <p className="text-stone-600 text-xs mt-1">
                              Pay deposit now, then £{((totals.subtotal - totals.totalDeposit) / Math.max(1, calculateMonthsUntilDate(event.payment_plan_end_date))).toFixed(2)}/month 
                              until {new Date(event.payment_plan_end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            </p>
                          </div>
                        )}
                        
                        {/* No Deposit Option - Special promotional option */}
                        {noDepositOption.enabled && (
                          <div 
                            className={`p-3 rounded-lg border ${
                              noDepositOption.canUse 
                                ? `cursor-pointer ${formData.payment_type === 'no_deposit' ? 'border-emerald-500 bg-emerald-500/10' : 'border-emerald-700 bg-emerald-900/20'}` 
                                : 'border-amber-700 bg-amber-900/20 cursor-not-allowed opacity-75'
                            }`}
                            onClick={() => {
                              if (noDepositOption.canUse) {
                                setFormData(prev => ({ ...prev, payment_type: 'no_deposit' }));
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-emerald-400 text-lg">⭐</span>
                                <span className="text-stone-900 font-medium">Book with No Deposit!</span>
                              </div>
                              <span className="text-emerald-400 font-semibold">£0.00</span>
                            </div>
                            {noDepositOption.canUse ? (
                              <p className="text-emerald-300 text-xs mt-1">
                                Special offer! Book now with no payment required. Pay the full amount closer to the event.
                              </p>
                            ) : (
                              <p className="text-amber-300 text-xs mt-1">
                                {noDepositOption.message || 'Sign in to unlock this special offer!'}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      {formData.payment_type === 'deposit_with_plan' && (
                        <p className="text-amber-400 text-xs mt-2">
                          Your card will be stored securely for automatic monthly payments on the 1st of each month.
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={() => setStep(2)}
                        className="flex-1 border-stone-200 text-stone-900 hover:bg-white"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={handleSubmitBooking}
                        disabled={submitting}
                        className="flex-1 bg-violet-600 hover:bg-violet-500"
                        data-testid="booking-submit-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Proceed to Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 4: Payment */}
                {step === 4 && booking && (
                  <div className="space-y-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-400 mb-2">
                        <Check className="w-5 h-5" />
                        <span className="font-medium">Booking Created!</span>
                      </div>
                      <p className="text-stone-600 text-sm">
                        Reference: <strong className="text-stone-900">{booking.booking_id}</strong>
                      </p>
                    </div>

                    <div className="bg-stone-50 rounded-lg p-4">
                      <h3 className="text-stone-900 font-medium mb-3">Payment Details</h3>
                      <div className="text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-stone-600">Amount Due Now</span>
                          <span className="text-violet-600 font-semibold">
                            £{(formData.payment_type === 'deposit' || formData.payment_type === 'deposit_with_plan')
                              ? (booking.total_deposit || booking.deposit_amount || 0).toFixed(2) 
                              : booking.total_amount.toFixed(2)}
                          </span>
                        </div>
                        {(formData.payment_type === 'deposit' || formData.payment_type === 'deposit_with_plan') && booking.room_deposit > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-stone-600">Room deposit</span>
                            <span className="text-stone-600">£{booking.room_deposit.toFixed(2)}</span>
                          </div>
                        )}
                        {(formData.payment_type === 'deposit' || formData.payment_type === 'deposit_with_plan') && booking.add_ons_deposit > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-stone-600">Add-ons deposit</span>
                            <span className="text-stone-600">£{booking.add_ons_deposit.toFixed(2)}</span>
                          </div>
                        )}
                        {formData.payment_type === 'deposit_with_plan' && (
                          <div className="mt-3 pt-3 border-t border-stone-200">
                            <p className="text-amber-400 text-xs mb-2">Payment Plan Setup</p>
                            <div className="flex justify-between text-xs">
                              <span className="text-stone-600">Remaining balance</span>
                              <span className="text-stone-600">£{(booking.total_amount - (booking.total_deposit || 0)).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-stone-600">Monthly payment</span>
                              <span className="text-stone-600">
                                £{((booking.total_amount - (booking.total_deposit || 0)) / Math.max(1, calculateMonthsUntilDate(event?.payment_plan_end_date))).toFixed(2)}/month
                              </span>
                            </div>
                            <p className="text-stone-600 text-xs mt-2">
                              Your card will be stored securely and charged automatically on the 1st of each month.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Square Payment Form */}
                    <SquarePaymentForm
                      amount={Math.round(((formData.payment_type === 'deposit' || formData.payment_type === 'deposit_with_plan')
                        ? (booking.total_deposit || booking.deposit_amount || 0) 
                        : booking.total_amount) * 100)}
                      currency="GBP"
                      bookingId={booking.booking_id}
                      paymentType={formData.payment_type === 'deposit_with_plan' ? 'deposit' : formData.payment_type}
                      storeCardMode={formData.payment_type === 'deposit_with_plan'}
                      onPaymentSuccess={async (data) => {
                        // If deposit_with_plan, we need to also create the payment plan
                        if (formData.payment_type === 'deposit_with_plan') {
                          if (data.card_id) {
                            try {
                              // Create the payment plan with the stored card
                              const planResponse = await fetch(`${API_URL}/api/weekends/bookings/${booking.booking_id}/payment-plan`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  card_id: data.card_id,
                                  customer_consent: true
                                })
                              });
                              const planData = await planResponse.json();
                              if (planData.success) {
                                toast.success('Payment plan set up successfully!');
                                setPaymentResult({ ...data, payment_plan: planData.plan });
                              } else {
                                console.error('Failed to create payment plan:', planData);
                                toast.error('Deposit paid. Payment plan setup failed - please contact us to set it up manually.');
                                setPaymentResult(data);
                              }
                            } catch (planError) {
                              console.error('Error creating payment plan:', planError);
                              toast.error('Deposit paid. Payment plan setup failed - please contact us to set it up manually.');
                              setPaymentResult(data);
                            }
                          } else {
                            // Card wasn't stored - payment still went through
                            console.warn('Card was not stored - card_id not returned');
                            toast.warning('Deposit paid. Card storage failed - please contact us to set up payment plan manually.');
                            setPaymentResult(data);
                          }
                        } else {
                          setPaymentResult(data);
                        }
                        setStep(5);
                      }}
                      onPaymentError={(error) => {
                        console.error('Payment error:', error);
                      }}
                    />

                    <p className="text-stone-600 text-xs text-center">
                      By completing this payment you confirm acceptance of our{' '}
                      <a href="/weekends/terms" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
                        Terms & Conditions
                      </a>.
                      A confirmation email will be sent to {booking.customer_email}.
                    </p>
                  </div>
                )}

                {/* Step 5: Success */}
                {step === 5 && booking && (
                  <div className="space-y-4 text-center">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-10 h-10 text-green-400" />
                    </div>
                    
                    <h3 className="text-xl font-semibold text-stone-900">Payment Successful!</h3>
                    
                    <div className="bg-stone-50 rounded-lg p-4 text-left">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-stone-600">Booking Reference</span>
                          <span className="text-stone-900 font-medium">{booking.booking_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-600">Amount Paid</span>
                          <span className="text-green-400 font-medium">
                            £{paymentResult?.amount_charged?.toFixed(2) || ((formData.payment_type === 'deposit' || formData.payment_type === 'deposit_with_plan')
                              ? (booking.total_deposit || booking.deposit_amount || 0).toFixed(2) 
                              : booking.total_amount.toFixed(2))}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-600">Status</span>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            {paymentResult?.new_status?.replace('_', ' ') || 'Confirmed'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Payment Plan Details */}
                    {paymentResult?.payment_plan && (
                      <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-4 text-left">
                        <h4 className="text-violet-600 font-medium mb-2 flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          Payment Plan Active
                        </h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-stone-600">Monthly Payment</span>
                            <span className="text-stone-900">£{paymentResult.payment_plan.monthly_amount?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-stone-600">Payments Remaining</span>
                            <span className="text-stone-900">{paymentResult.payment_plan.number_of_payments}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-stone-600">Next Payment</span>
                            <span className="text-stone-900">
                              {paymentResult.payment_plan.next_payment_date 
                                ? new Date(paymentResult.payment_plan.next_payment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                : '1st of next month'}
                            </span>
                          </div>
                        </div>
                        <p className="text-stone-600 text-xs mt-2">
                          Your card will be charged automatically on the 1st of each month.
                        </p>
                      </div>
                    )}

                    <p className="text-stone-600 text-sm">
                      A confirmation email has been sent to <strong className="text-stone-900">{booking.customer_email}</strong>
                    </p>

                    {/* Contact notice for email delivery issues */}
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-left">
                      <p className="text-amber-300 text-xs">
                        <strong>Haven't received your confirmation?</strong> Please check your spam/junk folder. 
                        If you still can't find it, contact us at{' '}
                        <a href="mailto:hi@linedancecentral.uk" className="underline hover:text-amber-200">
                          hi@linedancecentral.uk
                        </a>
                        {' '}with your booking reference above.
                      </p>
                    </div>

                    <Button
                      onClick={() => navigate('/weekends')}
                      className="w-full bg-violet-600 hover:bg-violet-500"
                    >
                      Back to Events
                    </Button>
                  </div>
                )}

                {/* Booking deadline notice */}
                {event.booking_deadline && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-amber-400">
                    <Clock className="w-4 h-4" />
                    <span>Book by {formatDate(event.booking_deadline)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
